import axios from 'axios';

// Support both absolute URL and relative paths (for same-origin deployment)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Auth token management
const getAuthHeader = () => {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ============ Auth API ============

export const checkAdminExists = async () => {
  try {
    const response = await axios.get(`${API}/auth/check`);
    return response.data;
  } catch (error) {
    console.error('Error checking admin:', error);
    throw error;
  }
};

export const setupAdmin = async (adminData) => {
  try {
    const response = await axios.post(`${API}/auth/setup`, adminData);
    return response.data;
  } catch (error) {
    console.error('Error setting up admin:', error);
    throw error;
  }
};

export const loginAdmin = async (credentials) => {
  try {
    const response = await axios.post(`${API}/auth/login`, credentials);
    if (response.data.access_token) {
      localStorage.setItem('admin_token', response.data.access_token);
      localStorage.setItem('admin_user', JSON.stringify(response.data.user));
    }
    return response.data;
  } catch (error) {
    console.error('Error logging in:', error);
    throw error;
  }
};

export const logoutAdmin = () => {
  localStorage.removeItem('admin_token');
  localStorage.removeItem('admin_user');
};

export const getCurrentAdmin = async () => {
  try {
    const response = await axios.get(`${API}/auth/me`, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error getting current admin:', error);
    throw error;
  }
};

export const changePassword = async (oldPassword, newPassword) => {
  try {
    const response = await axios.post(`${API}/auth/change-password`, null, {
      params: { old_password: oldPassword, new_password: newPassword },
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error changing password:', error);
    throw error;
  }
};

// ============ Services API ============

export const getServices = async (category = null, activeOnly = true) => {
  try {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    params.append('active_only', activeOnly);
    
    const response = await axios.get(`${API}/services?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching services:', error);
    throw error;
  }
};

export const getService = async (serviceId) => {
  try {
    const response = await axios.get(`${API}/services/${serviceId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching service:', error);
    throw error;
  }
};

// ============ Bookings API ============

export const createBooking = async (bookingData) => {
  try {
    const response = await axios.post(`${API}/bookings`, bookingData);
    return response.data;
  } catch (error) {
    console.error('Error creating booking:', error);
    throw error;
  }
};

export const getBookings = async (status = null) => {
  try {
    const params = status ? `?status=${status}` : '';
    const response = await axios.get(`${API}/bookings${params}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching bookings:', error);
    throw error;
  }
};

export const getBooking = async (bookingId) => {
  try {
    const response = await axios.get(`${API}/bookings/${bookingId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching booking:', error);
    throw error;
  }
};

export const updateBooking = async (bookingId, updateData) => {
  try {
    const response = await axios.patch(`${API}/bookings/${bookingId}`, updateData);
    return response.data;
  } catch (error) {
    console.error('Error updating booking:', error);
    throw error;
  }
};

// ============ Contacts API ============

export const submitContact = async (contactData) => {
  try {
    const response = await axios.post(`${API}/contacts`, contactData);
    return response.data;
  } catch (error) {
    console.error('Error submitting contact:', error);
    throw error;
  }
};

export const getContacts = async () => {
  try {
    const response = await axios.get(`${API}/contacts`);
    return response.data;
  } catch (error) {
    console.error('Error fetching contacts:', error);
    throw error;
  }
};

// ============ Health Check ============

export const checkHealth = async () => {
  try {
    const response = await axios.get(`${API}/health`);
    return response.data;
  } catch (error) {
    console.error('Error checking health:', error);
    throw error;
  }
};

// ============ Settings API ============

export const getSettings = async () => {
  try {
    const response = await axios.get(`${API}/settings`);
    return response.data;
  } catch (error) {
    console.error('Error fetching settings:', error);
    throw error;
  }
};

export const updateSettings = async (settingsData) => {
  try {
    const response = await axios.put(`${API}/settings`, settingsData);
    return response.data;
  } catch (error) {
    console.error('Error updating settings:', error);
    throw error;
  }
};

// ============ Admin API ============

export const deleteBooking = async (bookingId) => {
  try {
    const response = await axios.delete(`${API}/bookings/${bookingId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting booking:', error);
    throw error;
  }
};

export const createService = async (serviceData) => {
  try {
    const response = await axios.post(`${API}/services`, serviceData);
    return response.data;
  } catch (error) {
    console.error('Error creating service:', error);
    throw error;
  }
};

export const updateService = async (serviceId, serviceData) => {
  try {
    const response = await axios.patch(`${API}/services/${serviceId}`, serviceData);
    return response.data;
  } catch (error) {
    console.error('Error updating service:', error);
    throw error;
  }
};

export const deleteService = async (serviceId) => {
  try {
    const response = await axios.delete(`${API}/services/${serviceId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting service:', error);
    throw error;
  }
};

export const markContactRead = async (contactId) => {
  try {
    const response = await axios.patch(`${API}/contacts/${contactId}/read`);
    return response.data;
  } catch (error) {
    console.error('Error marking contact as read:', error);
    throw error;
  }
};

export const deleteContact = async (contactId) => {
  try {
    const response = await axios.delete(`${API}/contacts/${contactId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting contact:', error);
    throw error;
  }
};

// ============ Time Slots API ============

export const getTimeSlots = async (activeOnly = true) => {
  try {
    const response = await axios.get(`${API}/time-slots?active_only=${activeOnly}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching time slots:', error);
    throw error;
  }
};

export const createTimeSlot = async (slotData) => {
  try {
    const response = await axios.post(`${API}/time-slots`, slotData);
    return response.data;
  } catch (error) {
    console.error('Error creating time slot:', error);
    throw error;
  }
};

export const updateTimeSlot = async (slotId, slotData) => {
  try {
    const response = await axios.patch(`${API}/time-slots/${slotId}`, slotData);
    return response.data;
  } catch (error) {
    console.error('Error updating time slot:', error);
    throw error;
  }
};

export const deleteTimeSlot = async (slotId) => {
  try {
    const response = await axios.delete(`${API}/time-slots/${slotId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting time slot:', error);
    throw error;
  }
};

// ============ Blocked Dates API ============

export const getBlockedDates = async () => {
  try {
    const response = await axios.get(`${API}/blocked-dates`);
    return response.data;
  } catch (error) {
    console.error('Error fetching blocked dates:', error);
    throw error;
  }
};

export const createBlockedDate = async (dateData) => {
  try {
    const response = await axios.post(`${API}/blocked-dates`, dateData);
    return response.data;
  } catch (error) {
    console.error('Error blocking date:', error);
    throw error;
  }
};

export const deleteBlockedDate = async (date) => {
  try {
    const response = await axios.delete(`${API}/blocked-dates/${date}`);
    return response.data;
  } catch (error) {
    console.error('Error unblocking date:', error);
    throw error;
  }
};

export default {
  // Auth functions
  checkAdminExists,
  setupAdmin,
  loginAdmin,
  logoutAdmin,
  getCurrentAdmin,
  changePassword,
  // Service functions
  getServices,
  getService,
  createBooking,
  getBookings,
  getBooking,
  updateBooking,
  deleteBooking,
  submitContact,
  getContacts,
  markContactRead,
  deleteContact,
  createService,
  updateService,
  deleteService,
  getSettings,
  updateSettings,
  getTimeSlots,
  createTimeSlot,
  updateTimeSlot,
  deleteTimeSlot,
  getBlockedDates,
  createBlockedDate,
  deleteBlockedDate,
  checkHealth
};
